/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import LabelChip from '../../shared/label-chip';
import { OptionItem, EventData } from './card.types';
import Button from '../button';
import MenuOptions, { type SelectedItem, type MouseEventLI, renderIcon } from '../menus/menu-options';

export interface TagProps {
  label: string;
  color?: string;
}

export interface Props {
  id: string;
  title: string;
  subtitle: string;
  isLastItem: boolean;
  isFirstItem: boolean;
  onEvent: (dataType: OptionItem, eventData: EventData) => void;
  displayInitials?: boolean;
  actionItems: Array<OptionItem>;
  avatarSrc?: string;
  description?: string;
  captionIcon?: React.ReactElement;
  hideAvatar?: boolean;
  tags?: TagProps[];
}

function CardListItem({ isFirstItem, isLastItem, ...props }: Props) {
  const {
    id, title, subtitle, avatarSrc, description,
  } = props;

  return (
    <Paper
      elevation={0}
      square
      sx={{
        pt: 2,
        pb: 2,
        pl: 1.5,
        pr: 1.5,
        boxShadow: isLastItem ? '0px 4px 2px -1px rgba(0,0,0,0.28)' : undefined,
        borderTopLeftRadius: isFirstItem ? (theme) => theme.spacing(0.5) : undefined,
        borderTopRightRadius: isFirstItem ? (theme) => theme.spacing(0.5) : undefined,
        borderBottomRightRadius: isLastItem ? (theme) => theme.spacing(0.5) : undefined,
        borderBottomLeftRadius: isLastItem ? (theme) => theme.spacing(0.5) : undefined,
      }}
    >
      <Grid container spacing={1.5} alignItems={description ? 'flex-start' : 'center'}>
        <Grid
          item
          display="flex"
          {...description ? {
            mt: 0.5,
          } : {}}
          justifyContent="center"
        >
          {!props.hideAvatar && <Avatar alt={title} src={avatarSrc} />}
        </Grid>
        <Grid item style={{ flex: 1 }}>
          <Stack
            alignItems="start"
            {...description ? {
              gap: 0.5,
            } : {}}
          >
            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
              <Typography>{title}</Typography>
              {props.tags?.map((tag, index) => (
                <LabelChip
                  key={`${tag.label}-${index}`}
                  label={tag.label}
                  color={tag.color}
                />
              ))}
            </Box>
            {!!description && (
              <Typography variant="subtitle1" color="text.secondary">{description}</Typography>
            )}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              {...description ? {
                mt: 0.5,
              } : {}}
              sx={{
                display: 'flex', whiteSpace: 'pre-wrap', alignItems: 'center', gap: 0.5,
              }}
            >
              {!!props.captionIcon && (
                <>{props.captionIcon}</>
              )}
              {subtitle}
            </Typography>
          </Stack>
        </Grid>
        {!!props.actionItems.length && (
          <Grid
            item
            display="flex"
            justifyContent="flex-end"
          >
            {props.actionItems.length === 1 ? (
              <Button
                icon={renderIcon(props.actionItems[0])}
                data-type={props.actionItems[0]}
                onClick={() => props.onEvent(props.actionItems[0], {
                  id, title, subtitle, avatarSrc, description,
                })}
              />
            ) : (
              <MenuOptions
                options={props.actionItems}
                item={{ id, title, subtitle, avatarSrc, description }}
                onOptionClick={(e: MouseEventLI, selected: SelectedItem) => {
                  props.onEvent(selected.option, selected.item as EventData);
                }}
              />
            )}
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}

export default CardListItem;
